import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import defaultWelcomeOfferService from "./welcomeOfferService";
import useWelcomeOffer from "./useWelcomeOffer";
import WelcomeOfferModal from "./WelcomeOfferModal";
import "./welcomeOffer.css";

function hasVisiblePriorityDialog() {
  if (typeof document === "undefined") return false;
  return Array.from(
    document.querySelectorAll('[role="dialog"][aria-modal="true"]')
  ).some((node) => {
    if (node.closest(".welcome-offer-root")) return false;
    if (node.closest('[aria-hidden="true"]')) return false;
    return true;
  });
}

export default function WelcomeOfferController({
  enabled = true,
  service = defaultWelcomeOfferService,
}) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [priorityAvailable, setPriorityAvailable] = useState(true);
  const onSessionExpired = useCallback(async () => {
    try {
      await logout();
    } finally {
      navigate("/login", {
        replace: true,
        state: { reason: "session_expired" },
      });
    }
  }, [logout, navigate]);

  const welcomeOffer = useWelcomeOffer({
    enabled: enabled && Boolean(user),
    account: user,
    service,
    onSessionExpired,
  });

  useEffect(() => {
    if (!welcomeOffer.isOpen) {
      setPriorityAvailable(true);
      return undefined;
    }
    const check = () => setPriorityAvailable(!hasVisiblePriorityDialog());
    check();
    if (typeof MutationObserver !== "function") return undefined;
    const observer = new MutationObserver(check);
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ["aria-hidden", "aria-modal", "class"],
    });
    return () => observer.disconnect();
  }, [welcomeOffer.isOpen]);

  const priorityBlockedNow = welcomeOffer.isOpen && hasVisiblePriorityDialog();
  if (
    !welcomeOffer.isOpen ||
    !welcomeOffer.offer ||
    !priorityAvailable ||
    priorityBlockedNow
  ) {
    return null;
  }

  const discoverEternua = () => {
    void welcomeOffer.dismiss("discover");
    navigate("/");
  };

  return (
    <WelcomeOfferModal
      phase={welcomeOffer.phase}
      offer={welcomeOffer.offer}
      message={welcomeOffer.message}
      onClaim={welcomeOffer.claim}
      onReconcile={welcomeOffer.reconcile}
      onDismiss={welcomeOffer.dismiss}
      onDiscover={discoverEternua}
      onCloseSuccess={welcomeOffer.closeSuccess}
      onWallet={() => {
        welcomeOffer.closeSuccess();
        navigate("/wallet/transactions");
      }}
      onRewards={() => {
        welcomeOffer.closeSuccess();
        navigate("/recompenses/debloquees");
      }}
    />
  );
}
